/**
 * @fileoverview Own viewport-filling work comparison with compact selection controls across people, date ranges, and independent source records. Backend reports define source coverage; `WorkComparisonLayouts.js` owns alternate presentations and record inspection; `workComparisonData.js` owns date/availability semantics. Missing duration or unavailable coverage must remain distinct from zero work. The workspace compares recorded evidence rather than deriving a person's complete productivity or changing source systems.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../api';
import './WorkComparison.css';
import AppIcon from '../shared/AppIcon';
import WorkComparisonLayouts, { LAYOUTS, PeopleSelector, DailyGrid, RecordInspector, Totals, Metric, allRecords, sourceTotal } from './WorkComparisonLayouts';
import { SOURCES, dayKey, formatTime, comparisonDays, isAvailable, chartMaximum } from './workComparisonData';
export { dayKey, recordedWeek, formatTime, comparisonDays } from './workComparisonData';



function localDate(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}



function initialFilters() {
    const now = new Date();
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    let role = {};
    try { if (localStorage.getItem('work-comparison-role') === 'attorneys') role = { role: 'attorneys' }; } catch (_) { /* Storage may be disabled. */ }
    return { start: localDate(monday), end: localDate(sunday), view: 'team', ...role,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles' };
}



function PersonComparison({ person, filters }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [selection, setSelection] = useState(null);
    useEffect(

    () => {
        let active = true;
        setData(null); setError(''); setSelection(null);
        const { view, ...range } = filters;
        api.get('/admin/work-comparison', { params: { ...range, employee_id: person.id } }).then(({ data: result }) => {
            if (active) setData(result);
        }).catch(failure => { if (active) setError(failure.response?.data?.error || 'This person’s records could not be loaded.'); });
        return () => { active = false; };
    }, [person.id, filters]);
    const days = useMemo(() => comparisonDays(data), [data]);
    if (error) return <p role="alert" className="work-comparison__message">{error}</p>;
    if (!data) return <p role="status" className="work-comparison__message">Loading {person.name}’s records…</p>;
    const future = data.start > dayKey(new Date(), data.timezone);
    const detailed = { ...data.employee, days, sources: Object.fromEntries(SOURCES.map(s => [s.key, { ...data.sources[s.key], ...(future ? { status: 'future' } : {}), value: future ? null : sourceTotal(s.key, data.sources[s.key]?.records || []) }])) };
    const records = allRecords([detailed]);
    const onDay = 

    (p, key, source) => {
        const day = days.find(d => d.key === key);
        setSelection({ key: `${p.id}:${key}${source ? ':' + source : ''}`, title: `${p.name} · ${key}${source ? ' · ' + SOURCES.find(s => s.key === source).label : ''}`, states: source ? { [source]: day.sources[source] } : day.sources, records: records.filter(r => dayKey(r.at, data.timezone) === key && (!source || r.source === source)) });
    };
    return <div className="work-comparison__person-detail">
        <h3>{data.employee.name}</h3>
        <Totals person={detailed} />
        <DailyGrid people={[detailed]} onDay={onDay} selectedKey={selection?.key} />
        <RecordInspector selection={selection} onClose={() => setSelection(null)} zone={data.timezone} />
    </div>;
}



export default function WorkComparison() {
    const [draft, setDraft] = useState(initialFilters);
    const [filters, setFilters] = useState(initialFilters);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [expanded, setExpanded] = useState(null);
    const [layout, setLayout] = useState('weekly');
    const [refresh, setRefresh] = useState(0);
    const [roster, setRoster] = useState([]);
    const [selectedIds, setSelectedIds] = useState(null);
    const requestNumber = useRef(0);
    const rangePicker = useRef(null);
    const toolbar = useRef(null);
    const [sort, setSort] = useState({ key: 'name', direction: 'asc' });
    const updatedAt = data?.team?.flatMap(person => SOURCES.map(source => person.sources[source.key]?.updated_at)).filter(Boolean).sort()[0];
    useEffect(

    () => {
        const dismiss = 

        event => toolbar.current?.querySelectorAll('details[open]').forEach(

        details => {
            if (event.type === 'keydown' && event.key === 'Escape') {
                details.open = false;
                if (details.contains(document.activeElement)) details.querySelector('summary')?.focus();
            } else if (event.type === 'pointerdown' && !details.contains(event.target)) details.open = false;
        });
        document.addEventListener('pointerdown', dismiss);
        document.addEventListener('keydown', dismiss);
        return () => { document.removeEventListener('pointerdown', dismiss); document.removeEventListener('keydown', dismiss); };
    }, []);

    useEffect(

    () => {
        const current = ++requestNumber.current;
        setLoading(true);
        setError('');
        setData(null);
        setExpanded(null);
        api.get('/admin/work-comparison', { params: filters }).then(({ data: result }) => {
            if (current === requestNumber.current) {
                setData(result);
                setRoster(result.team || []);
            }
        }).catch((failure) => {
            if (current === requestNumber.current) setError(failure.response?.data?.error || 'The comparison could not be loaded.');
        }).finally(() => { if (current === requestNumber.current) setLoading(false); });
        return () => { requestNumber.current += 1; };
    }, [filters, refresh]);

    const update = 

    (key, value) => setDraft((current) => ({ ...current, [key]: value }));
    const setRole = 

    (role) => {
        const applyRole = 

        (current) => { const { role: previous, ...rest } = current; return role === 'attorneys' ? { ...rest, role } : rest; };
        setDraft(applyRole);
        setFilters(applyRole);
        try { localStorage.setItem('work-comparison-role', role); } catch (_) { /* Filtering still works without storage. */ }
    };
    const label = 

    (key, includeYear = true) => new Date(`${key}T12:00:00Z`).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', ...(includeYear ? { year: 'numeric' } : {}) });
    const visiblePeople = (data?.team || []).filter(person => selectedIds === null || selectedIds.includes(person.id)).sort(

    (a, b) => {
        if (sort.key === 'name') return (sort.direction === 'asc' ? 1 : -1) * a.name.localeCompare(b.name);
        const left = isAvailable(a.sources[sort.key]) ? a.sources[sort.key].value : null;
        const right = isAvailable(b.sources[sort.key]) ? b.sources[sort.key].value : null;
        if (left == null || right == null) return left == null && right == null ? a.name.localeCompare(b.name) : left == null ? 1 : -1;
        return (sort.direction === 'asc' ? left - right : right - left) || a.name.localeCompare(b.name);
    });
    const maximum = 

    source => {
        const value = Math.max(0, ...(data?.team || []).map(person => person.sources[source]?.value || 0));
        return source === 'clockify' ? chartMaximum(value / 3600) * 3600 : chartMaximum(value);
    };
    const sortBy = 

    key => setSort(current => ({ key, direction: current.key === key && current.direction === 'asc' ? 'desc' : current.key === key ? 'asc' : key === 'name' ? 'asc' : 'desc' }));
    const sortArrow = 

    key => sort.key === key ? sort.direction === 'asc' ? '↑' : '↓' : '↕';
    const future = filters.start > dayKey(new Date(), filters.timezone);
    const moveWeek = 

    (offset) => {
        const shift = 

        (value) => { const day = new Date(`${value}T12:00:00Z`); day.setUTCDate(day.getUTCDate() + offset); return day.toISOString().slice(0, 10); };
        const next = { ...filters, start: shift(filters.start), end: shift(filters.end) };
        setDraft(next);
        setFilters(next);
    };
    return <section className="work-comparison" aria-label="Time and work comparison">
        <div className="work-comparison__toolbar" ref={toolbar}>
            <header className="work-comparison__header">
                <h2>Time & work</h2>
                <nav className="work-layout-picker" aria-label="Comparison layouts">
                    {LAYOUTS.map(option => <button type="button" key={option.id} aria-pressed={layout === option.id} onClick={() => setLayout(option.id)}>{option.label}</button>)}
                </nav>
                <button type="button" className="work-comparison__refresh" aria-label="Refresh" title="Refresh" onClick={() => setRefresh(value => value + 1)} disabled={loading}><AppIcon icon="pi pi-refresh" aria-hidden="true" /></button>
            </header>
            <div className="work-comparison__toolbar-row">
                <div className="work-comparison__role-filter" role="group" aria-label="People filter">
                    <button type="button" aria-pressed={filters.role !== 'attorneys'} onClick={() => setRole('all')}>Whole team</button>
                    <button type="button" aria-pressed={filters.role === 'attorneys'} onClick={() => setRole('attorneys')}>Attorneys only</button>
                </div>
                <PeopleSelector people={roster} selectedIds={selectedIds} onChange={ids => { setSelectedIds(ids); setExpanded(null); }} loading={loading} />
                <div className="work-comparison__week-nav">
                    <button type="button" aria-label="Previous week" title="Previous week" onClick={() => moveWeek(-7)} disabled={loading}><AppIcon icon="pi pi-chevron-left" aria-hidden="true" /></button>
                    <details className="work-comparison__range" ref={rangePicker}><summary aria-label="Choose dates" title={`${label(filters.start)} – ${label(filters.end)}`}>{label(filters.start, filters.start.slice(0, 4) !== filters.end.slice(0, 4))} – {label(filters.end)} <AppIcon icon="pi pi-chevron-down" aria-hidden="true" /></summary>
                        <form className="work-comparison__filters work-comparison__filters--team" onSubmit={event => { event.preventDefault(); setFilters({ ...draft }); rangePicker.current.open = false; }}>
                            <label>From<input type="date" value={draft.start} onChange={event => update('start', event.target.value)} required /></label>
                            <label>Through<input type="date" value={draft.end} min={draft.start} onChange={event => update('end', event.target.value)} required /></label>
                            <label>Timezone<select value={draft.timezone} onChange={event => update('timezone', event.target.value)}>{[...new Set([draft.timezone, 'America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York', 'UTC'])].map(zone => <option key={zone}>{zone}</option>)}</select></label>
                            <button type="submit" disabled={loading}>Compare</button>
                        </form>
                    </details>
                    <button type="button" aria-label="Next week" title="Next week" onClick={() => moveWeek(7)} disabled={loading}><AppIcon icon="pi pi-chevron-right" aria-hidden="true" /></button>
                </div>
                <span className="work-comparison__zone" title={filters.timezone}>{filters.timezone.replace('America/', '').replaceAll('_', ' ')}{updatedAt && <> · <time dateTime={updatedAt} title={`Totals updated ${new Date(updatedAt).toLocaleString()}`}>↻ {new Date(updatedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</time></>}</span>
            </div>
        </div>
        <div className="work-comparison__results">
        {error && <div role="alert" className="work-comparison__message">{error}</div>}
        {loading && <div role="status" className="work-comparison__message">Loading team records…</div>}
        {!loading && data && <>
            <WorkComparisonLayouts layout={layout} filters={filters} refresh={refresh} selectedIds={selectedIds} summaryData={data} />
            <div className="work-comparison__weekly" hidden={layout !== 'weekly'}>
            <div className="work-comparison__team" role="group" aria-label="Team weekly comparison"><table>
                <thead><tr>
                    <th scope="col" aria-sort={sort.key === 'name' ? sort.direction === 'asc' ? 'ascending' : 'descending' : 'none'}><button type="button" onClick={() => sortBy('name')}>Person <span>{sortArrow('name')}</span></button></th>
                    {SOURCES.map(source => <th key={source.key} scope="col" aria-sort={sort.key === source.key ? sort.direction === 'asc' ? 'ascending' : 'descending' : 'none'}>
                        <button type="button" onClick={() => sortBy(source.key)}>{source.label} · {source.unit} <span>{sortArrow(source.key)}</span></button>
                        <div className="work-comparison__scale"><span>0</span><span>{source.key === 'clockify' ? formatTime(maximum(source.key)) : maximum(source.key)}</span></div>
                    </th>)}
                </tr></thead><tbody>
                {visiblePeople.map(person => <React.Fragment key={person.id}><tr className={expanded === person.id ? 'is-expanded' : ''}>
                    <th scope="row"><button type="button" className="work-comparison__person" aria-expanded={expanded === person.id} aria-controls={`work-person-${person.id}`} aria-label={`${expanded === person.id ? 'Collapse' : 'Expand'} ${person.name}`} onClick={() => setExpanded(current => current === person.id ? null : person.id)}>
                        <span className="work-comparison__person-name"><span aria-hidden="true">{expanded === person.id ? '⌄' : '›'}</span>{person.name}</span>
                    </button></th>
                    {SOURCES.map(source => {
                        const state = future ? { status: 'future' } : person.sources[source.key];
                        return <td key={source.key} className={`work-comparison__weekly-metric work-comparison__bar-cell--${source.key}`}>
                            <div className="work-comparison__weekly-value"><Metric source={source.key} state={state} />
                            {isAvailable(state) && state.value != null && <span className="work-comparison__bar-track" aria-hidden="true"><span style={{ width: `${100 * state.value / maximum(source.key)}%` }} /></span>}</div>
                        </td>;
                    })}
                </tr>{expanded === person.id && <tr><td colSpan={4}><section id={`work-person-${person.id}`} aria-label={`${person.name} details`}><PersonComparison person={person} filters={filters} /></section></td></tr>}</React.Fragment>)}
                </tbody>
            </table>
                {!data.team?.length && <p className="work-comparison__message">{filters.role === 'attorneys' ? 'No active attorneys.' : 'No active employees.'}</p>}
                {!!data.team?.length && !visiblePeople.length && <p className="work-comparison__message">Select people above to compare.</p>}
            </div>
            </div>
        </>}
        </div>
    </section>;
}
